import React from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  ogImage?: string;
  schema?: Record<string, unknown>;
  robots?: string;
}

export function SEOHead({ title, description, ogImage, schema, robots = "index, follow" }: SEOHeadProps) {
  React.useEffect(() => {
    // Update meta tags dynamically
    document.title = title;
    const canonicalBase = "https://irfanqutab.dev";
    const pathname = window.location.pathname || "/";
    const normalizedPath = pathname !== "/" ? pathname.replace(/\/$/, "") : "/";
    const canonicalUrl = `${canonicalBase}${normalizedPath}`;
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", description);
    }

    // Set robots meta tag
    const robotsTag = document.querySelector('meta[name="robots"]');
    if (robotsTag) {
      robotsTag.setAttribute("content", robots);
    } else {
      const newRobotsTag = document.createElement('meta');
      newRobotsTag.setAttribute('name', 'robots');
      newRobotsTag.setAttribute('content', robots);
      document.head.appendChild(newRobotsTag);
    }
    
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute("content", title);
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute("content", description);
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute("content", canonicalUrl);
    }
    
    if (ogImage) {
      const ogImageTag = document.querySelector('meta[property="og:image"]');
      if (ogImageTag) {
        ogImageTag.setAttribute("content", ogImage);
      }

      const twitterImage = document.querySelector('meta[name="twitter:image"]');
      if (twitterImage) {
        twitterImage.setAttribute("content", ogImage);
      }

      // Preload the OG image for faster LCP on blog posts
      let preloadLink = document.querySelector('link[rel="preload"][as="image"][data-og-image]');
      if (!preloadLink) {
        preloadLink = document.createElement('link');
        preloadLink.setAttribute('rel', 'preload');
        preloadLink.setAttribute('as', 'image');
        preloadLink.setAttribute('data-og-image', 'true');
        document.head.appendChild(preloadLink);
      }
      preloadLink.setAttribute('href', ogImage);
    }

    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute("content", title);
    }

    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute("content", description);
    }

    const twitterUrl = document.querySelector('meta[name="twitter:url"]');
    if (twitterUrl) {
      twitterUrl.setAttribute("content", canonicalUrl);
    }

    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute("href", canonicalUrl);
    } else {
      const link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      link.setAttribute("href", canonicalUrl);
      document.head.appendChild(link);
    }
    
    // Add structured data
    if (schema) {
      const structuredData = document.querySelector('script[type="application/ld+json"]');
      if (!structuredData) {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      } else {
        structuredData.textContent = JSON.stringify(schema);
      }
    }
  }, [title, description, ogImage, schema, robots]);

  return null;
}
